<script lang="ts">
	import { onMount } from 'svelte';
	import { afterNavigate } from '$app/navigation';
	import { page } from '$app/stores';
	import { tracker } from './tracker.svelte';
	import type { MatomoWindow } from './types';
	import { PUBLIC_MATOMO_SITE_ID, PUBLIC_MATOMO_URL } from '$env/static/public';

	let {
		url = PUBLIC_MATOMO_URL,
		siteId = PUBLIC_MATOMO_SITE_ID,
		disableCookies = false,
		requireConsent = false,
		doNotTrack = false,
		enableCrossDomainLinking = false,
		domains = [],
		heartBeat = 15,
		linkTracking = null
	}: {
		url?: string;
		siteId?: string;
		disableCookies?: boolean;
		requireConsent?: boolean;
		doNotTrack?: boolean;
		enableCrossDomainLinking?: boolean;
		domains?: string[];
		heartBeat?: number | null;
		linkTracking?: boolean | null;
	} = $props();

	let initializing: Promise<void> | null = null;

	const isPrerendering = (): boolean => !!document.prerendering;

	function loadMatomoScript(): Promise<void> {
		return new Promise((resolve, reject) => {
			if ((window as unknown as MatomoWindow).Matomo) {
				resolve();
				return;
			}
			const script = document.createElement('script');
			script.src = `${url}/m.js`;
			script.async = true;
			script.onload = () => resolve();
			script.onerror = () => reject(new Error('[Matomo] Failed to load m.js'));
			document.head.appendChild(script);
		});
	}

	async function initializeMatomo() {
		if (tracker.current) return;
		if (initializing) {
			await initializing;
			return;
		}

		initializing = (async () => {
			try {
				await loadMatomoScript();
			} catch (err) {
				console.warn(err);
				return;
			}

			const matomo = (window as unknown as MatomoWindow).Matomo;
			if (!matomo) {
				console.warn('[Matomo] Matomo object not found on window');
				return;
			}

			const track = matomo.getTracker(`${url}`, siteId as unknown as number);
			if (!track) {
				console.warn('[Matomo] Failed to get tracker');
				return;
			}

			if (disableCookies) track.disableCookies();
			if (requireConsent) track.requireConsent();
			if (doNotTrack) track.setDoNotTrack(true);
			if (heartBeat) track.enableHeartBeatTimer(heartBeat);
			if (enableCrossDomainLinking) track.enableCrossDomainLinking();
			if (domains.length) track.setDomains(domains);
			if (linkTracking !== null) track.enableLinkTracking(linkTracking);

			tracker.set(track);

			if (isPrerendering()) {
				document.addEventListener('prerenderingchange', () => {
					track.setCustomUrl($page.url.href);
					track.trackPageView();
					console.log('[Matomo] Initialized and tracked page view (after prerender activated)');
				}, { once: true });
				return;
			}

			track.setCustomUrl($page.url.href);
			track.trackPageView();
			console.log('[Matomo] Initialized and tracked initial page view');
		})();

		await initializing;
	}

	onMount(() => {
		requestIdleCallback(() => {
			setTimeout(initializeMatomo, 200);
		});
	});

	afterNavigate(async ({ to }) => {
		if (isPrerendering()) return;

		if (!tracker.current) {
			await initializeMatomo();
			return;
		}

		if (to?.url.href && tracker.current) {
			tracker.current.setCustomUrl(to.url.href);
			tracker.current.trackPageView();
			console.log('[Matomo] Page view tracked:', to.url.href);
		}
	});
</script>

<svelte:head>
	{#if url}
		<link rel="preconnect" href={url} crossorigin="anonymous" />
		<link rel="dns-prefetch" href={url} />
	{/if}
</svelte:head>